import { v4 as uuid } from 'uuid'
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useDispatch } from "react-redux"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import TradingAccountSelector from "../../tradingAccountStatus/TradingAccountSelector"
import OpenTradingAccountDialog from "../../tradingAccountStatus/OpenTradingAccountDialog"
import { useTradingAccountContext } from "../../../hooks/useTradingAccountContext"
import { LoadingButton } from "@mui/lab"
import { resetStrategyBuilder, setBasketId, setBatchId, setLoading, setWaitingForOrders, updateTradeOrder, updateTradeOrders } from "../../../reducer/strategyBuilder.reducer"
import { selectMargins, selectPrimaryTradingAccount, selectTradingAccounts, selectUserDetails } from "../../../selectors/user"
import { useSelector } from "react-redux"
import { PlaceOrModifyOrdersRequest, TradingAccount, TradingAccountStatus } from "../../../proto/generated/trading-bridge"
import { selectBasketConditionalEntry, selectBasketConditionalExit, selectBasketId, selectBatchId, selectExitStrategy, selectIceberg, selectOrderType, selectStrategyBuilderError, selectStrategyBuilderLoading, selectStrategyBuilderMode, selectTradeOrders, selectTradeProduct, selectWaitingForOrders, selectStrategyInfo } from "../../../selectors/strategyBuilder"
import { selectOrdersHistory } from "../../../selectors/orders"
import { selectPositions } from "../../../selectors/positions"
import { Broker_OrderType, Broker_Variety, Order, OrderStatus, OrderType, Product, TransactionType, Validity } from "../../../proto/generated/order"
import commonActions from "../../../actions/common.actions"
import AnalyticsManager from "../../../analytics/AnalyticsManager"
import { updateOrderHistory } from "../../../reducer/orders.reducer"
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { cancelOrder, placeOrModifyOrder } from '../../../services/apis/orders'
import { WidgetListItems } from '../../../util/sidebar'
import useSelectPrimaryOrUnderlyingSecurity from '../../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import { isBrokerSessionReady } from '../../../util/orderPlacement'
import { isShoonyaBackend } from '../../../util/backendProvider'
import { useHasAdminAccess } from '../../../hooks/useHasAdminAccess'
import { getStrategyName } from '../../../services/apis/misc'
import { getIcebergLegOrdersForSecurity, ICEBERG_NON_TERMINAL_STATUSES } from '../../../util/icebergOrders'
import { buildQuickTradeConditionalPlacePlan } from '../../../util/quickTradeConditionalPlace'
import ChargesBreakdownDrawer from '../../ChargesBreakdownTooltip/ChargesBreakdownDrawer'
import {
    batchAllowsConditionalExitPlacement,
    batchAllowsNewConditionalEntryLeg,
    basketAllowsStandaloneCxPlace,
    basketBookHasOpenExposureForBasket,
    basketPrimaryLegsAllAllowEntryRuleEdit,
    conditionalEntryLegAllowsRuleEditInUi,
    conditionalExitLegAllowsRuleEditInUi,
    findLiveConditionalAuxiliaryLeg,
    hasConditionalRuleContent,
    isConditionalEntryReady,
    orderExecutionParamsForConditionalEntry,
    resolveAuxiliaryLegBatchId,
    resolveAuxiliaryLegWireIds,
    resolveBatchIdForBasketFromOrders,
    resolveConditionalAuxProduct,
} from '../../../util/conditionalEntry'
import {
    addOrderAlertMessage,
    isFullBasketSquareOff,
    resolveOrderLegTag,
    stampOrderLegTags,
} from '../../../util/orders'

import { blue } from '../../../assets/colors'
import RefreshIcon from '../../../assets/icons/RefreshIcon'
import { getAccountMargins } from '../../../services/apis/tradingAccount'
import { setMarginForAccount } from '../../../reducer/user.reducer'

export type OrderBarProps = {
    tradingAccount?: TradingAccount,
    disabled?: boolean
    hasSlAndTargetAccess: boolean
    isSlTargetValid: boolean
    drawerAnchorRef?: React.RefObject<HTMLElement | null>
}

/** At this width and above, margin cluster uses normal (12px) type; below uses compact (11px). */
const ORDER_BAR_MARGIN_NORMAL_FONT_MIN_PX = 480

const OrderBar: React.FC<OrderBarProps> = ( { tradingAccount, hasSlAndTargetAccess, isSlTargetValid, drawerAnchorRef }) => {

    const dispatch = useDispatch();
    const user = useSelector(selectUserDetails);
    const { hasActiveTradingAccount } = useTradingAccountContext()
    const [openAccountDialog, setOpenAccountDialog] = useState(false)
    const { primaryOrUnderlying: selectedSecurity } = useSelectPrimaryOrUnderlyingSecurity();
    const mode = useSelector(selectStrategyBuilderMode);
    const loading = useSelector(selectStrategyBuilderLoading);
    const error = useSelector(selectStrategyBuilderError);
    const basketId = useSelector(selectBasketId);
    const batchId = useSelector(selectBatchId);
    const product = useSelector(selectTradeProduct);
    const orderType = useSelector(selectOrderType);
    const waitingForOrders = useSelector(selectWaitingForOrders);
    const placedOrders = useSelector(selectOrdersHistory);
    const positions = useSelector(selectPositions);
    const effectiveBatchId = useMemo(
        () => batchId || resolveBatchIdForBasketFromOrders(placedOrders, basketId) || '',
        [batchId, placedOrders, basketId]
    )
    const tradeOrders = useSelector(selectTradeOrders);
    const tradingAccounts = useSelector(selectTradingAccounts)
    const { addStopLoss, addTarget, slValue, targetValue } = useSelector(selectExitStrategy);
    const iceberg = useSelector(selectIceberg);
    const basketConditionalEntry = useSelector(selectBasketConditionalEntry);
    const basketConditionalExit = useSelector(selectBasketConditionalExit);
    const [slClientOrderId, setSlClientOrderId] = useState(uuid());
    const [targetClientOrderId, setTargetClientOrderId] = useState(uuid());
    const [ceClientOrderId, setCeClientOrderId] = useState(uuid());
    const [cxClientOrderId, setCxClientOrderId] = useState(uuid());
    const [strategyName, setStrategyName] = useState("");
    const [orderBarWidth, setOrderBarWidth] = useState(800);
    const [chargesDrawerOpen, setChargesDrawerOpen] = useState(false);
    const toggleChargesDrawer = useCallback((event: React.MouseEvent) => {
        event.stopPropagation()
        setChargesDrawerOpen((open) => !open)
    }, [])
    const isAdminUser = useHasAdminAccess()

    const optionPositionsRequest = useMemo(() => {
        return {
            optionPosition: tradeOrders.map(t => ({
                optionType: t.security.optionType,
                transactionType: t.transactionType,
                strikePrice: t.security.strikePrice,
                expiry: t.security.expiry.expiry,
                lots: t.quantity
            }))
        }
    }, [tradeOrders])

    useEffect(() => {
        if (!optionPositionsRequest.optionPosition.length) {
            setStrategyName("")
            return
        }
        const fetchStrategy = async () => {
            try {
                const res = await getStrategyName({
                    body: optionPositionsRequest
                })
                setStrategyName(res.strategyName)
            } catch (e) {
                console.error("Strategy API failed", e)
                setStrategyName("")
            }
        }
        fetchStrategy()
    }, [optionPositionsRequest.optionPosition])

    const effectiveStrategyGroup = useMemo(() => {
        const sym = selectedSecurity?.symbol ?? ''
        if (strategyName === 'Custom') {
            return `${sym} Basket`
        }
        return `${sym} - ${strategyName}`
    }, [strategyName, selectedSecurity?.symbol])

    const basketInactiveSlOrder = useMemo(() => {
        return (placedOrders || []).find(o => {
            return o.basketId === basketId && 
            o.status === OrderStatus.INACTIVE && o.orderType === OrderType.STOP_LOSS
        })
    }, [basketId, placedOrders]);
    
    const basketInactiveTargetOrder = useMemo(() => {
        return (placedOrders || []).find(o => {
            return o.basketId === basketId && 
            o.status === OrderStatus.INACTIVE && o.orderType === OrderType.TARGET
        })
    }, [basketId, placedOrders]);
    
    
    const orderIdToOrderMap = useMemo(() => {
        if (!placedOrders) return {} as Record<string, Order>;
        
        return placedOrders.reduce((acc, o) => {
            acc[o.orderId] = o;
            return acc;
        }, {} as Record<string, Order>)
    }, [placedOrders])
    
    const batchOrdersMap = useMemo(() => {
        if (!placedOrders || !effectiveBatchId) return {} as Record<string, Order>;
        return placedOrders.reduce((acc, o) => {
            if (o.batchId === effectiveBatchId) {
                acc[o.orderId] = o;
            }
            return acc;
        }, {} as Record<string, Order>);
    }, [placedOrders, effectiveBatchId])

    const tradeRowPendingOrActive = useCallback((o: (typeof tradeOrders)[0]) => {
        if (!o.selected) return false
        const legs = getIcebergLegOrdersForSecurity(placedOrders || [], effectiveBatchId, basketId, o.security)
        if (legs.length > 0) {
            return legs.some((l) => ICEBERG_NON_TERMINAL_STATUSES.includes(l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN))
        }
        if (!o.referenceOrderId) return true
        const st = batchOrdersMap[o.referenceOrderId]?.status ?? OrderStatus.UNRECOGNIZED
        return ![OrderStatus.CANCELLED, OrderStatus.EXECUTED, OrderStatus.REJECTED, OrderStatus.FAILED].includes(st)
    }, [placedOrders, effectiveBatchId, basketId, batchOrdersMap])

    const allExecuted = useMemo(() => {
        const selected = tradeOrders.filter((o) => o.selected)
        if (selected.length === 0) return false
        return !selected.some((o) => {
            const legs = getIcebergLegOrdersForSecurity(placedOrders || [], effectiveBatchId, basketId, o.security)
            if (legs.length > 0) {
                return !legs.every((l) => l.status === OrderStatus.EXECUTED)
            }
            if (!o.referenceOrderId) return true
            return batchOrdersMap[o.referenceOrderId]?.status !== OrderStatus.EXECUTED
        })
    }, [tradeOrders, batchOrdersMap, placedOrders, effectiveBatchId, basketId])

    const basketOpenSlOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            return o.basketId === basketId && [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.STOP_LOSS
        })
    }, [placedOrders, basketId])

    const basketOpenTargetOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            return o.basketId === basketId && [OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status) && o.orderType === OrderType.TARGET
        })
    }, [placedOrders, basketId])

    const liveBasketCeOrder = useMemo(
        () =>
            findLiveConditionalAuxiliaryLeg(placedOrders, {
                basketId,
                batchId: effectiveBatchId,
                orderType: OrderType.CONDITIONAL_ENTRY,
                isBasket: true,
            }),
        [placedOrders, basketId, effectiveBatchId]
    )

    const liveBasketCxOrder = useMemo(
        () =>
            findLiveConditionalAuxiliaryLeg(placedOrders, {
                basketId,
                batchId: effectiveBatchId,
                orderType: OrderType.CONDITIONAL_EXIT,
                isBasket: true,
            }),
        [placedOrders, basketId, effectiveBatchId]
    )

    const basketOpenCeOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            if (o.basketId !== basketId) return false
            if (
                ![OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(o.status ?? 0)
            )
                return false
            if (o.orderType !== OrderType.CONDITIONAL_ENTRY) return false
            if (effectiveBatchId && o.batchId !== effectiveBatchId) return false
            return true
        })
    }, [placedOrders, basketId, effectiveBatchId])

    const basketInactiveCeOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            if (o.basketId !== basketId) return false
            if (o.status !== OrderStatus.INACTIVE || o.orderType !== OrderType.CONDITIONAL_ENTRY) return false
            return !!effectiveBatchId && o.batchId === effectiveBatchId
        })
    }, [placedOrders, basketId, effectiveBatchId])

    const basketOpenCxOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            if (o.basketId !== basketId) return false
            if (
                ![OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING].includes(
                    o.status ?? 0
                )
            )
                return false
            if (o.orderType !== OrderType.CONDITIONAL_EXIT) return false
            if (effectiveBatchId && o.batchId !== effectiveBatchId) return false
            return true
        })
    }, [placedOrders, basketId, effectiveBatchId])

    const basketInactiveCxOrder = useMemo(() => {
        return (placedOrders || []).find((o) => {
            if (o.basketId !== basketId) return false
            if (o.status !== OrderStatus.INACTIVE || o.orderType !== OrderType.CONDITIONAL_EXIT) return false
            return !!effectiveBatchId && o.batchId === effectiveBatchId
        })
    }, [placedOrders, basketId, effectiveBatchId])

    const slMatches = useMemo(() => {
        if (!addStopLoss || !slValue || !basketOpenSlOrder) return false
        const expectedTriggerPrice = Math.round(Number(slValue) * (selectedSecurity?.priceMultiplier ?? 100))
        return basketOpenSlOrder.triggerPrice === expectedTriggerPrice
    }, [addStopLoss, slValue, basketOpenSlOrder, selectedSecurity])

    const targetMatches = useMemo(() => {
        if (!addTarget || !targetValue || !basketOpenTargetOrder) return false
        const expectedPrice = Number(targetValue) * (selectedSecurity?.priceMultiplier ?? 100)
        return basketOpenTargetOrder.price === expectedPrice
    }, [addTarget, targetValue, basketOpenTargetOrder, selectedSecurity])

    const shouldPlaceSlTarget = useMemo(() => {

        const pendingOrders = tradeOrders.some((o) => tradeRowPendingOrActive(o))
        if (pendingOrders) return false

        // EXISTING STATE
        const hasExistingSL = !!basketOpenSlOrder
        const hasExistingTarget = !!basketOpenTargetOrder

        // UI STATE
        const wantsSL = addStopLoss && !!slValue
        const wantsTarget = addTarget && !!targetValue

        // SL intent
        const slNeedsAction =
            (hasExistingSL && !wantsSL) || // REMOVE SL
            (!hasExistingSL && wantsSL) || // ADD SL
            (hasExistingSL && wantsSL && !slMatches) // MODIFY SL

        // Target intent
        const targetNeedsAction =
            (hasExistingTarget && !wantsTarget) || // REMOVE Target
            (!hasExistingTarget && wantsTarget) || // ADD Target
            (hasExistingTarget && wantsTarget && !targetMatches) // MODIFY Target

        return slNeedsAction || targetNeedsAction
    }, [mode, tradeOrders, batchOrdersMap, addStopLoss, slValue, addTarget, targetValue, basketOpenSlOrder, basketOpenTargetOrder, slMatches, targetMatches])

    const shouldPlaceConditionalEntry = useMemo(() => {
        if (!hasSlAndTargetAccess) return false
        if (
            (orderType !== Broker_OrderType.LIMIT_ORDER && orderType !== Broker_OrderType.MARKET_ORDER) ||
            iceberg.enabled
        )
            return false
        const wants = basketConditionalEntry.enabled && isConditionalEntryReady(basketConditionalEntry)
        const has = !!liveBasketCeOrder
        if (!wants && !has) return false
        // Remove CE (cancel on place)
        if (has && !wants) return true
        if (!has && wants) {
            if (!batchAllowsNewConditionalEntryLeg(placedOrders || [], basketId, effectiveBatchId))
                return false
            return true
        }
        if (has && wants) {
            if (liveBasketCeOrder && !conditionalEntryLegAllowsRuleEditInUi(liveBasketCeOrder)) return false
            if (
                basketId &&
                effectiveBatchId &&
                !basketPrimaryLegsAllAllowEntryRuleEdit(placedOrders || [], basketId, effectiveBatchId)
            ) {
                return false
            }
            const a = liveBasketCeOrder?.orderExecutionParams?.condition
            const b = basketConditionalEntry.condition
            if (!hasConditionalRuleContent(a) || !hasConditionalRuleContent(b)) return true
            return JSON.stringify(a) !== JSON.stringify(b)
        }
        return false
    }, [
        hasSlAndTargetAccess,
        orderType,
        iceberg.enabled,
        basketConditionalEntry,
        liveBasketCeOrder,
        placedOrders,
        basketId,
        effectiveBatchId,
    ])

    const shouldPlaceConditionalExit = useMemo(() => {
        if (!hasSlAndTargetAccess) return false
        // Do not gate on tradeOrders.length: first basket place sends mains + CE + CX in one request while
        // the grid still has rows.
        if (
            (orderType !== Broker_OrderType.LIMIT_ORDER && orderType !== Broker_OrderType.MARKET_ORDER) ||
            iceberg.enabled
        )
            return false
        const existingCxRef = liveBasketCxOrder
        if (existingCxRef && !conditionalExitLegAllowsRuleEditInUi(existingCxRef) && basketConditionalExit.enabled) {
            return false
        }
        const wants = basketConditionalExit.enabled && isConditionalEntryReady(basketConditionalExit)
        const has = !!liveBasketCxOrder
        if (!wants && !has) return false
        // Remove CX (cancel on place) — needed for Analyze CX removal
        if (has && !wants) return true

        const eligibleForNewCx = batchAllowsConditionalExitPlacement(
            placedOrders || [],
            basketId,
            effectiveBatchId
        )

        // New CX: only pre-place or all mains executed (not while mains working without CX).
        if (!has && wants) {
            if (!eligibleForNewCx) return false
            const placingMains = tradeOrders.some((o) => o.selected)
            if (
                !placingMains &&
                !basketAllowsStandaloneCxPlace(placedOrders, positions, basketId) &&
                !basketBookHasOpenExposureForBasket(positions, basketId)
            ) {
                return false
            }
            return true
        }
        // Modify existing CX: allowed while mains open/partial (leg editability already checked above).
        if (has && wants) {
            if (!conditionalExitLegAllowsRuleEditInUi(existingCxRef)) return false
            const a = existingCxRef?.orderExecutionParams?.condition
            const b = basketConditionalExit.condition
            if (!hasConditionalRuleContent(a) || !hasConditionalRuleContent(b)) return true
            return JSON.stringify(a) !== JSON.stringify(b)
        }
        return false
    }, [
        hasSlAndTargetAccess,
        orderType,
        iceberg.enabled,
        placedOrders,
        positions,
        basketId,
        effectiveBatchId,
        basketConditionalExit,
        liveBasketCxOrder,
        tradeOrders,
    ])

    const orderButtonText = useMemo(() => {
        let text = hasActiveTradingAccount ? (!isAdminUser ? "Practice" : "Place") : "Place";

        if (waitingForOrders) {
            let retry = false
            let modify = false
            const failedStatuses = [OrderStatus.FAILED, OrderStatus.CANCELLED, OrderStatus.REJECTED]
            if (tradeOrders.filter(o => o.selected).some(o => {
                const legs = getIcebergLegOrdersForSecurity(placedOrders || [], effectiveBatchId, basketId, o.security)
                if (legs.length > 0) {
                    return legs.some(l => failedStatuses.includes(l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN))
                }
                return failedStatuses.includes(batchOrdersMap[o.referenceOrderId]?.status ?? OrderStatus.UNRECOGNIZED)
            })) {
                retry = true
            }
            if (tradeOrders.filter(o => o.selected).some(o => {
                const legs = getIcebergLegOrdersForSecurity(placedOrders || [], effectiveBatchId, basketId, o.security)
                if (legs.length > 0) {
                    return legs.some(l => ICEBERG_NON_TERMINAL_STATUSES.includes(l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN))
                }
                return ICEBERG_NON_TERMINAL_STATUSES.includes(batchOrdersMap[o.referenceOrderId]?.status ?? OrderStatus.ORDER_STATUS_UNKNOWN)
            })) {
                modify = true
            }

            if (retry && modify) {
                text = 'Retry/Modify'
            } else if (retry) {
                text = "Retry"
            } else if (modify) {
                text = "Modify"
            }
        }
        const pendingOrders = tradeOrders.filter((o) => tradeRowPendingOrActive(o))
        if (mode === 'analyze') {
            const hasNewOrders = pendingOrders.length > 0;
            if (hasNewOrders) {
                return `${text} ${pendingOrders.length > 1 ? "Orders": "Order"}`;
            } else {
                return "Modify Positions"
            }
        }
        if(allExecuted && mode == 'trade'){
            if(shouldPlaceSlTarget || shouldPlaceConditionalEntry || shouldPlaceConditionalExit){
                return "Modify Positions"
            }else{
                return "Go to Positions"
            } 
        }

        return allExecuted ? "Modify Positions" : `${text} ${pendingOrders.length > 1 ? "Orders": "Order"}`;
    }, [batchOrdersMap, tradeOrders, mode, waitingForOrders, allExecuted, addStopLoss, addTarget, slValue, targetValue, basketOpenSlOrder, basketOpenTargetOrder, slMatches, targetMatches, placedOrders, effectiveBatchId, basketId, tradeRowPendingOrActive, shouldPlaceSlTarget, shouldPlaceConditionalEntry, shouldPlaceConditionalExit, isAdminUser, hasActiveTradingAccount]);

    /** Shown only when the primary action is place/retry orders (margin is meaningful), not Modify / Go to Positions. */
    const showMarginCluster = useMemo(() => {
        const pendingOrders = tradeOrders.filter(
            (o) =>
                (!o.referenceOrderId && o.selected) ||
                (batchOrdersMap[o.referenceOrderId] &&
                    ![OrderStatus.CANCELLED, OrderStatus.EXECUTED, OrderStatus.REJECTED].includes(
                        batchOrdersMap[o.referenceOrderId]?.status
                    ))
        )
        if (mode === 'analyze') {
            return pendingOrders.length > 0
        }
        if (allExecuted && mode === 'trade') {
            return false
        }
        return !allExecuted
    }, [mode, tradeOrders, batchOrdersMap, allExecuted])

    const goToPositionsHandler = useCallback(() => {
        dispatch(resetStrategyBuilder());
        commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.POSITIONS)
    }, [])

    const onPlaceOrderClick = useCallback(async () => {
        const shoonyaSessionReady = isShoonyaBackend() && isBrokerSessionReady(tradingAccount)
        if (!hasActiveTradingAccount && !shoonyaSessionReady) {
            AnalyticsManager.logEvent('Place Order Clicked')
            setOpenAccountDialog(true)
            return
        }
        if (!shoonyaSessionReady && ((tradingAccounts?.length === 0) || (tradingAccount?.status !== TradingAccountStatus.ACTIVE))) {
            AnalyticsManager.logEvent('Place Order Clicked')
            return;
        } else if (error) {
            return
        }

        // Handle cancelling of SL and Target orders when deselected
        const cancelOrderIds: string[] = []
        
        // Cancel existing SL order if checkbox is deselected
        if (basketOpenSlOrder && !addStopLoss && basketOpenSlOrder?.orderId) {
            cancelOrderIds.push(basketOpenSlOrder.orderId)
        }

        // Cancel existing Target order if checkbox is deselected
        if (basketOpenTargetOrder && !addTarget && basketOpenTargetOrder?.orderId) {
            cancelOrderIds.push(basketOpenTargetOrder.orderId)
        }

        // Cancel basket CE when conditional entry is turned off (same batch only for inactive CE)
        if (basketOpenCeOrder && !basketConditionalEntry.enabled && basketOpenCeOrder.orderId) {
            cancelOrderIds.push(basketOpenCeOrder.orderId)
        }
        if (
            basketInactiveCeOrder &&
            !basketConditionalEntry.enabled &&
            basketInactiveCeOrder.orderId &&
            basketInactiveCeOrder.batchId === effectiveBatchId &&
            basketInactiveCeOrder.orderId !== basketOpenCeOrder?.orderId
        ) {
            cancelOrderIds.push(basketInactiveCeOrder.orderId)
        }

        if (basketOpenCxOrder && !basketConditionalExit.enabled && basketOpenCxOrder.orderId) {
            cancelOrderIds.push(basketOpenCxOrder.orderId)
        }
        if (
            basketInactiveCxOrder &&
            !basketConditionalExit.enabled &&
            basketInactiveCxOrder.orderId &&
            basketInactiveCxOrder.batchId === effectiveBatchId &&
            basketInactiveCxOrder.orderId !== basketOpenCxOrder?.orderId
        ) {
            cancelOrderIds.push(basketInactiveCxOrder.orderId)
        }

        // Cancel orders first if needed
        if (cancelOrderIds.length) {
            const cancelResult = await cancelOrder({
                dispatch,
                body: {
                    tradingAccountId: tradingAccount?.accountId ?? '',
                    orderId: cancelOrderIds
                }
            })
            
            // Update order history immediately with cancelled status
            const cancelledOrders: Order[] = []
            cancelResult.orderResponse.forEach((response) => {
                if (response.order && response.successful) {
                    cancelledOrders.push({
                        ...response.order,
                        status: OrderStatus.CANCELLED
                    })
                }
            })
            
            if (cancelledOrders.length) {
                dispatch(updateOrderHistory({ updates: cancelledOrders }))
            }
        }

        const updatedBasketId = basketId || uuid();
        const updatedBatchId = batchId || effectiveBatchId || uuid();
        AnalyticsManager.logEvent('Place Order Clicked');
        const orderDate = Date.now();
        let updateRejectedOrders = false; // flag to update trade orders if any rejected or failed order is present.

        const newTradeOrders = tradeOrders.map((to) => {
            const referenceOrder = to.referenceOrderId ? orderIdToOrderMap[to.referenceOrderId] : undefined
            // Margin/place can persist FAILED with this clientOrderId before referenceOrderId is set.
            // Remint so retry from the same trade-panel legs does not hit "already placed".
            const priorFailedSameClientId = (placedOrders || []).find(
                (o) =>
                    o.clientOrderId === to.clientOrderId &&
                    [OrderStatus.REJECTED, OrderStatus.FAILED].includes(
                        o.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
                    )
            )
            if (
                (referenceOrder &&
                    [OrderStatus.REJECTED, OrderStatus.FAILED].includes(referenceOrder.status)) ||
                priorFailedSameClientId
            ) {
                updateRejectedOrders = true
                return {
                    ...to,
                    referenceOrderId: undefined,
                    clientOrderId: uuid(),
                }
            }
            return to
        })
        const selectedOrders = newTradeOrders.filter((t) => {
            if (!t.selected) return false
            const legs = getIcebergLegOrdersForSecurity(placedOrders || [], effectiveBatchId, basketId, t.security)
            if (legs.length > 0) {
                return legs.some((l) => {
                    const st = l.status ?? OrderStatus.ORDER_STATUS_UNKNOWN
                    return (
                        ICEBERG_NON_TERMINAL_STATUSES.includes(st) ||
                        st === OrderStatus.FAILED ||
                        st === OrderStatus.REJECTED
                    )
                })
            }
            return !t.referenceOrderId || orderIdToOrderMap[t.referenceOrderId]?.status !== OrderStatus.EXECUTED
        })
        
        // Build order details list
        const orderDetailsList: Order[] = [];
        
        if (iceberg.enabled && iceberg.numberOfLegs >= 1) {
            // Iceberg basket: one child order per leg × security; modify/cancel only non-executed legs
            for (let legIndex = 0; legIndex < iceberg.numberOfLegs; legIndex++) {
                for (const tradeOrder of selectedOrders) {
                    if (!tradeOrder.security) {
                        console.error('Security is not defined for order', tradeOrder)
                        throw new Error('Security is not defined for order');
                    }
                    const security = await getSecurityAsync(getSecurityKey(tradeOrder.security.exchange, tradeOrder.security.code))
                    if (!security) {
                        console.error('Security not present for trading', tradeOrder)
                        throw new Error('Security is not defined for order');
                    }
                    const resolvedProduct = resolveConditionalAuxProduct(product)
                    const legOrdersForSecurity = getIcebergLegOrdersForSecurity(
                        placedOrders || [],
                        effectiveBatchId,
                        basketId,
                        tradeOrder.security
                    );
                    const thisLegPlaced = legOrdersForSecurity.find(
                        (l) => (l.orderExecutionParams?.icebergLegNumber ?? 0) === legIndex + 1
                    );
                    if (thisLegPlaced?.status === OrderStatus.EXECUTED) {
                        continue;
                    }
                    let referenceOrder: Order | undefined;
                    if (
                        thisLegPlaced &&
                        (ICEBERG_NON_TERMINAL_STATUSES.includes(thisLegPlaced.status ?? OrderStatus.ORDER_STATUS_UNKNOWN) ||
                            thisLegPlaced.status === OrderStatus.FAILED ||
                            thisLegPlaced.status === OrderStatus.REJECTED)
                    ) {
                        referenceOrder = thisLegPlaced;
                    } else if (!thisLegPlaced && legIndex === 0 && tradeOrder.referenceOrderId) {
                        const ref = orderIdToOrderMap[tradeOrder.referenceOrderId];
                        if (
                            ref &&
                            (ICEBERG_NON_TERMINAL_STATUSES.includes(ref.status ?? OrderStatus.ORDER_STATUS_UNKNOWN) ||
                                ref.status === OrderStatus.FAILED ||
                                ref.status === OrderStatus.REJECTED)
                        ) {
                            referenceOrder = ref;
                        }
                    }

                    const timestamp = Date.now();
                    const uniqueId = uuid();
                    const legClientOrderId = `iceberg-${legIndex + 1}-${timestamp}-${uniqueId.substring(0, 8)}-${security.code.substring(0, 8)}`;

                    orderDetailsList.push({
                        security: {
                            code: security.code,
                            exchange: security.exchange
                        },
                        transactionType: tradeOrder.transactionType,
                        orderType: OrderType.ICEBERG,
                        brokerOrderType: orderType,
                        quantity: tradeOrder.quantity,
                        product: resolvedProduct,
                        price: orderType === Broker_OrderType.MARKET_ORDER ? 0 : tradeOrder.price,
                        triggerPrice: 0,
                        brokerVariety: Broker_Variety.REGULAR,
                        validity: Validity.DAY,
                        clientOrderId: legClientOrderId,
                        orderDate: referenceOrder?.orderDate ?? orderDate,
                        isBasket: true,
                        orderId: referenceOrder?.orderId,
                        basketId: updatedBasketId,
                        batchId: updatedBatchId,
                        strategyGroup: effectiveStrategyGroup,
                        immediateExecutePaperOrder: false,
                        orderExecutionParams: {
                            icebergNumberOfLegs: iceberg.numberOfLegs,
                            icebergLegNumber: legIndex + 1
                        },
                        ...(referenceOrder?.tag !== undefined ? { tag: referenceOrder.tag } : {}),
                    });
                }
            }
        } else {
            // Regular basket orders (non-iceberg)
            for (const tradeOrder of selectedOrders) {
                if (!tradeOrder.security) {
                    console.error('Security is not defined for order', tradeOrder)
                    throw new Error('Security is not defined for order');
                }
                const security = await getSecurityAsync(getSecurityKey(tradeOrder.security.exchange, tradeOrder.security.code))
                if (!security) {
                    console.error('Security not present for trading', tradeOrder)
                    throw new Error('Security is not defined for order');
                }
                const resolvedProduct = resolveConditionalAuxProduct(product)
                const referenceOrder = tradeOrder.referenceOrderId ? orderIdToOrderMap[tradeOrder.referenceOrderId] : undefined;
                
                orderDetailsList.push({
                    security: {
                        code: security.code,
                        exchange: security.exchange
                    },
                    transactionType: tradeOrder.transactionType,
                    orderType: OrderType.NORMAL,
                    brokerOrderType: orderType,
                    quantity: tradeOrder.quantity,
                    product: resolvedProduct,
                    price: orderType === Broker_OrderType.MARKET_ORDER ? 0 : tradeOrder.price,
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: tradeOrder.clientOrderId,
                    orderDate: referenceOrder ? referenceOrder.orderDate : orderDate,
                    isBasket: true,
                    orderId: referenceOrder ? referenceOrder.orderId : undefined,
                    basketId: updatedBasketId,
                    batchId: updatedBatchId,
                    strategyGroup: effectiveStrategyGroup,
                    immediateExecutePaperOrder: false,
                    ...(referenceOrder?.tag !== undefined ? { tag: referenceOrder.tag } : {}),
                });
            }
        }
        
        const request: PlaceOrModifyOrdersRequest = {
            tradingAccountId: tradingAccount?.accountId,
            buyBeforeSell: true,
            order: orderDetailsList
        }

        const batchIdForBasketConditionalLegs = resolveAuxiliaryLegBatchId({
            orders: placedOrders,
            basketId: updatedBasketId,
            submissionPrimaryBatchId: orderDetailsList.length > 0 ? updatedBatchId : undefined,
        })

        const skipSlTargetForBasketCeCxOnlyModify =
            orderDetailsList.length === 0 &&
            (shouldPlaceConditionalEntry || shouldPlaceConditionalExit)

        // Only add SL order if checkbox is selected AND value is provided
        if (
            !skipSlTargetForBasketCeCxOnlyModify &&
            addStopLoss &&
            slValue &&
            hasSlAndTargetAccess
        ) {
            const existingSlOrder = basketOpenSlOrder || (basketInactiveSlOrder?.batchId === effectiveBatchId ? basketInactiveSlOrder : undefined)
            request.order.push({
                security: {
                    code: selectedSecurity?.code ?? '',
                    exchange: selectedSecurity?.exchange ?? 0
                },
                transactionType: TransactionType.BUY,
                orderType: OrderType.STOP_LOSS,
                brokerOrderType: Broker_OrderType.STOPLOSS_MARKET_ORDER,
                quantity: 1,
                isBasket: true,
                basketId: updatedBasketId,
                batchId: updatedBatchId,
                orderDate: Date.now(),
                product: resolveConditionalAuxProduct(product),
                price: 0,
                triggerPrice: Math.round(Number(slValue) * (selectedSecurity?.priceMultiplier ?? 100)),
                validity: Validity.DAY,
                brokerVariety: Broker_Variety.REGULAR,
                clientOrderId: existingSlOrder?.clientOrderId ?? slClientOrderId,
                orderExecutionParams: {},
                orderId: existingSlOrder?.orderId,
                strategyGroup: effectiveStrategyGroup,
                immediateExecutePaperOrder: false,
                marketProtection: -1,
            })
        }

        // Only add Target order if checkbox is selected AND value is provided
        if (
            !skipSlTargetForBasketCeCxOnlyModify &&
            addTarget &&
            targetValue &&
            hasSlAndTargetAccess
        ) {
            const existingTargetOrder = basketOpenTargetOrder || (basketInactiveTargetOrder?.batchId === effectiveBatchId ? basketInactiveTargetOrder : undefined)

            request.order.push({
                security: {
                    code: selectedSecurity?.code ?? '',
                    exchange: selectedSecurity?.exchange ?? 0
                },
                transactionType: TransactionType.BUY,
                orderType: OrderType.TARGET,
                brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                quantity: 1,
                isBasket: true,
                basketId: updatedBasketId,
                batchId: updatedBatchId,
                orderDate: Date.now(),
                product: resolveConditionalAuxProduct(product),
                price: Number(targetValue)*(selectedSecurity?.priceMultiplier??100),
                triggerPrice: 0,
                validity: Validity.DAY,
                brokerVariety: Broker_Variety.REGULAR,
                clientOrderId: existingTargetOrder?.clientOrderId ?? targetClientOrderId,
                orderExecutionParams: {},
                orderId: existingTargetOrder?.orderId,
                strategyGroup: effectiveStrategyGroup,
                immediateExecutePaperOrder: false,
            })
        }

        if (shouldPlaceConditionalEntry && basketConditionalEntry.enabled) {
            const existingCe = liveBasketCeOrder
            const ceWire = resolveAuxiliaryLegWireIds({
                existingLeg: existingCe,
                fallbackClientOrderId: ceClientOrderId,
            })
            const plan = buildQuickTradeConditionalPlacePlan({
                placeType: 'trade',
                isSquareOffOrder: false,
                mode: 'regular',
                orderType: OrderType.NORMAL,
                modifyingOrderType: existingCe ? OrderType.CONDITIONAL_ENTRY : undefined,
                conditionalState: basketConditionalEntry,
                baseExecutionParams: existingCe?.orderExecutionParams,
                useSeparateConditionalEntryLeg: true,
            })
            const ceProduct = resolveConditionalAuxProduct(product)
            request.order.push({
                security: {
                    code: selectedSecurity?.code ?? '',
                    exchange: selectedSecurity?.exchange ?? 0,
                },
                transactionType: TransactionType.BUY,
                orderType: OrderType.CONDITIONAL_ENTRY,
                brokerOrderType: Broker_OrderType.MARKET_ORDER,
                quantity: 1,
                isBasket: true,
                basketId: updatedBasketId,
                batchId: resolveAuxiliaryLegBatchId({
                    orders: placedOrders,
                    basketId: updatedBasketId,
                    existingAuxLeg: existingCe,
                    submissionPrimaryBatchId: batchIdForBasketConditionalLegs,
                }),
                orderDate: Date.now(),
                product: ceProduct,
                price: 0,
                triggerPrice: 0,
                validity: Validity.DAY,
                brokerVariety: Broker_Variety.REGULAR,
                clientOrderId: ceWire.clientOrderId,
                orderExecutionParams: plan.entryParams ?? {},
                orderId: ceWire.orderId,
                strategyGroup: effectiveStrategyGroup,
                immediateExecutePaperOrder: false,
                ...(existingCe?.tag !== undefined ? { tag: existingCe.tag } : {}),
            })
        }

        if (shouldPlaceConditionalExit && basketConditionalExit.enabled) {
            const existingCx = liveBasketCxOrder
            const cxWire = resolveAuxiliaryLegWireIds({
                existingLeg: existingCx,
                fallbackClientOrderId: cxClientOrderId,
            })
            const cxParams = orderExecutionParamsForConditionalEntry(
                existingCx?.orderExecutionParams,
                basketConditionalExit,
                { stripWhenDisabled: true }
            )
            const cxProduct = resolveConditionalAuxProduct(product)
            request.order.push({
                security: {
                    code: selectedSecurity?.code ?? '',
                    exchange: selectedSecurity?.exchange ?? 0,
                },
                transactionType: TransactionType.BUY,
                orderType: OrderType.CONDITIONAL_EXIT,
                brokerOrderType: Broker_OrderType.MARKET_ORDER,
                quantity: 1,
                isBasket: true,
                basketId: updatedBasketId,
                batchId: resolveAuxiliaryLegBatchId({
                    orders: placedOrders,
                    basketId: updatedBasketId,
                    existingAuxLeg: existingCx,
                    submissionPrimaryBatchId: batchIdForBasketConditionalLegs,
                }),
                orderDate: Date.now(),
                product: cxProduct,
                price: 0,
                triggerPrice: 0,
                validity: Validity.DAY,
                brokerVariety: Broker_Variety.REGULAR,
                clientOrderId: cxWire.clientOrderId,
                orderExecutionParams: cxParams ?? {},
                orderId: cxWire.orderId,
                strategyGroup: effectiveStrategyGroup,
                immediateExecutePaperOrder: false,
                ...(existingCx?.tag !== undefined ? { tag: existingCx.tag } : {}),
            })
        }
        try {
            dispatch(setLoading(true));
            if (updateRejectedOrders) {
                dispatch(updateTradeOrders(newTradeOrders));
            }
            if (!request.order.length) {
                return
            }
            const existingBasket = Boolean(
                basketId && positions.some((p) => p.basketId === basketId && p.closePosition !== 0)
            )
            stampOrderLegTags(
                request.order,
                resolveOrderLegTag({
                    scope: 'basket',
                    existingBasket: existingBasket || mode === 'analyze' || mode === 'squareOff',
                    fullExit:
                        mode === 'squareOff' &&
                        isFullBasketSquareOff(request.order, positions, basketId),
                })
            )
            const result = await placeOrModifyOrder(request)
            const orderUpdates: Order[] = [];
            
            // For iceberg baskets, we need to map responses back to trade orders
            if (iceberg.enabled && iceberg.numberOfLegs >= 1) {
                // Orders are organized as: [leg1-security1, leg1-security2, ..., leg2-security1, leg2-security2, ...]
                result.orderResponse.forEach((response, index) => {
                    const { order, successful, errorMessage } = response
                    if (!successful) {
                        const reqOrder = request.order[index]
                        addOrderAlertMessage({
                            ...(order ?? reqOrder),
                            status: OrderStatus.FAILED,
                            message: errorMessage || 'Unable to place order',
                        })
                        if (index < selectedOrders.length) {
                            const tradeOrder = selectedOrders[index]
                            dispatch(
                                updateTradeOrder({
                                    clientOrderId: tradeOrder.clientOrderId,
                                    update: {
                                        clientOrderId: uuid(),
                                        referenceOrderId: undefined,
                                    },
                                })
                            )
                        }
                        return
                    }
                    if (!order) return
                    
                    // Calculate which trade order this belongs to
                    const tradeOrderIndex = index % selectedOrders.length;
                    const tradeOrder = selectedOrders[tradeOrderIndex];
                    
                    // Only update referenceOrderId for the first leg
                    if (index < selectedOrders.length) {
                        dispatch(updateTradeOrder({
                            clientOrderId: tradeOrder.clientOrderId,
                            update: {
                                referenceOrderId: order.orderId
                            }
                        }));
                    }
                    orderUpdates.push(order);
                });
            } else {
                // Regular basket orders
                result.orderResponse.forEach((response, index) => {
                    const { order, successful, errorMessage } = response
                    if (!successful) {
                        const reqOrder = request.order[index]
                        addOrderAlertMessage({
                            ...(order ?? reqOrder),
                            status: OrderStatus.FAILED,
                            message: errorMessage || 'Unable to place order',
                        })
                        // Primary legs only — remint so next Place does not reuse FAILED clientOrderId
                        if (index < selectedOrders.length) {
                            const tradeOrder = selectedOrders[index]
                            dispatch(
                                updateTradeOrder({
                                    clientOrderId: tradeOrder.clientOrderId,
                                    update: {
                                        clientOrderId: uuid(),
                                        referenceOrderId: undefined,
                                    },
                                })
                            )
                        }
                        return
                    }
                    if (!order) return
                    dispatch(updateTradeOrder({
                        clientOrderId: order.clientOrderId,
                        update: {
                            referenceOrderId: order.orderId
                        }
                    }));
                    orderUpdates.push(order);
                });
            }
            
            if (orderUpdates.length) {
                dispatch(updateOrderHistory({updates: orderUpdates}))
                !waitingForOrders && dispatch(setWaitingForOrders(true))
                dispatch(setBasketId(updatedBasketId))
                dispatch(setBatchId(updatedBatchId))
            }
        } catch (error) {
            console.log(error , "error occurred")
            setCeClientOrderId(uuid())
            setCxClientOrderId(uuid())
            commonActions.setApiError(dispatch, {
                error: true,
                message: "Error placing orders",
                errorType: 'error'
            })
        } finally {
            dispatch(setLoading(false));
        }

    }, [mode, basketId, batchId, effectiveBatchId, tradeOrders, orderIdToOrderMap, tradingAccount, error, waitingForOrders, product,
        orderType, slValue, basketInactiveSlOrder, targetValue, basketInactiveTargetOrder, isAdminUser, hasSlAndTargetAccess, basketOpenSlOrder, basketOpenTargetOrder, selectedSecurity, slClientOrderId, targetClientOrderId, addStopLoss, addTarget, tradingAccounts, effectiveStrategyGroup, iceberg, placedOrders, shouldPlaceConditionalEntry, basketConditionalEntry, liveBasketCeOrder, ceClientOrderId, shouldPlaceConditionalExit, basketConditionalExit, liveBasketCxOrder, cxClientOrderId, hasActiveTradingAccount, positions])

    // Determine if button should be disabled
    const isButtonDisabled = useMemo(() => {
        // In analyze mode, disable only if no new orders AND no SL/Target changes
        if (mode === 'analyze' || mode == 'squareOff') {
            const pendingOrders = tradeOrders.filter((o) => tradeRowPendingOrActive(o))

            const hasNewOrders = pendingOrders.length > 0;
            const hasSlTargetChanges = shouldPlaceSlTarget;
            const hasConditionalEntryChanges = shouldPlaceConditionalEntry;
            const hasConditionalExitChanges = shouldPlaceConditionalExit;

            if ((!hasNewOrders && !hasSlTargetChanges && !hasConditionalEntryChanges && !hasConditionalExitChanges) || !isSlTargetValid) {
                return true;
            }
        }
        return loading || !!error;
    }, [mode, tradeOrders, batchOrdersMap, shouldPlaceSlTarget, shouldPlaceConditionalEntry, shouldPlaceConditionalExit, loading, error, isSlTargetValid, tradeRowPendingOrActive]);

    const margins = useSelector(selectMargins)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const strategyInfo = useSelector(selectStrategyInfo)

    const primaryTradingAccountMarginAvailable = useMemo(() => {
        return margins[primaryTradingAccount?.accountId]?.marginAvailable
    }, [primaryTradingAccount, margins])

    const handleAllMarginsRefresh = useCallback(async () => {
        if (isShoonyaBackend()) return
        if (!primaryTradingAccount || primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            return
        }
        try {
            const body = {
                accountId: primaryTradingAccount.accountId,
                brokerCode: primaryTradingAccount.brokerCode,
                created: primaryTradingAccount.created
            }
            const response = await getAccountMargins({ body })
            dispatch(setMarginForAccount({ accountId: primaryTradingAccount.accountId, margin: response }))
        } catch (e) {
            console.error('Failed to refresh margin', e)
        }
    }, [primaryTradingAccount, dispatch])

    const marginCluster = useMemo(() => {
        if (!showMarginCluster) return null
        const isAccountActive = primaryTradingAccount?.status === TradingAccountStatus.ACTIVE
        const hasRequiredMargin = isAccountActive && tradeOrders.length > 0
        const hasAvailableMargin = isAccountActive && primaryTradingAccountMarginAvailable !== undefined
        const fs = orderBarWidth >= ORDER_BAR_MARGIN_NORMAL_FONT_MIN_PX ? 12 : 11
        const labelTypographyProps = {
            color: 'text.secondary' as const,
            fontSize: fs,
            fontWeight: 'medium' as const,
        }
        const valueTypographyProps = { fontSize: fs, fontWeight: 'medium' as const, color: blue }
        const ellipsisSx = {
            overflow: 'hidden' as const,
            textOverflow: 'ellipsis' as const,
            whiteSpace: 'nowrap' as const,
            maxWidth: fs >= 12 ? 88 : 72,
            display: 'inline-block' as const,
            verticalAlign: 'bottom' as const,
        }
        const reqValueText = `₹${strategyInfo.totalMargin.toLocaleString('en-IN')}`
        const availValueText = `₹${(primaryTradingAccountMarginAvailable ?? 0).toLocaleString('en-IN')}`
        const reqTooltipFull = `Required: ${reqValueText}`

        return (
            <Stack
                direction="row"
                alignItems="center"
                justifyContent={'flex-end'}
                spacing={2}
                flexWrap="nowrap"
                useFlexGap
                marginBottom={0.5}
                sx={{ minWidth: 0, maxWidth: '100%' }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                        pr: !isAccountActive ? 1 : 0,
                        minWidth: 0,
                        flexShrink: 1,
                        ...(hasRequiredMargin
                            ? { cursor: 'pointer', borderRadius: '4px', '&:hover': { opacity: 0.85 } }
                            : {}),
                    }}
                    onClick={hasRequiredMargin ? toggleChargesDrawer : undefined}
                >
                    <Typography {...labelTypographyProps} component="span">
                        Required
                    </Typography>
                    {hasRequiredMargin ? (
                        <Tooltip title={`${reqTooltipFull} · Click for breakdown`} arrow placement="top">
                            <Typography sx={{ ...valueTypographyProps, ...ellipsisSx }} component="span">
                                {reqValueText}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title={
                                !tradeOrders.length
                                    ? 'Add orders to estimate required margin'
                                    : 'Margin unavailable'
                            }
                            arrow
                            placement="top"
                        >
                            <Typography sx={{ ...valueTypographyProps, ...ellipsisSx }} component="span">
                                —
                            </Typography>
                        </Tooltip>
                    )}
                    {hasRequiredMargin && strategyInfo.brokerCharges != null && (
                        <Typography
                            sx={{ fontSize: fs, fontWeight: 'medium', color: 'text.secondary' }}
                            component="span"
                        >
                            {`+${Number(strategyInfo.brokerCharges).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flexShrink: 1 }}>
                    <Typography {...labelTypographyProps} component="span">
                        Available
                    </Typography>
                    {hasAvailableMargin ? (
                        <Tooltip title={`Available: ${availValueText}`} arrow placement="top">
                            <Typography sx={{ ...valueTypographyProps, ...ellipsisSx }} component="span">
                                {availValueText}
                            </Typography>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Margin unavailable" arrow placement="top">
                            <Typography sx={{ ...valueTypographyProps, ...ellipsisSx }} component="span">
                                —
                            </Typography>
                        </Tooltip>
                    )}
                    {isAccountActive && (
                        <IconButton size="small" onClick={handleAllMarginsRefresh} sx={{marginLeft:'4px !important', padding:0}}>
                            <RefreshIcon sx={{ width: 16, height: 16, stroke: blue }} />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        )
    }, [
        primaryTradingAccount,
        tradeOrders.length,
        strategyInfo.totalMargin,
        strategyInfo.brokerCharges,
        strategyInfo.costBreakdown,
        primaryTradingAccountMarginAvailable,
        handleAllMarginsRefresh,
        orderBarWidth,
        showMarginCluster,
        toggleChargesDrawer,
    ])

    const tradingAccountSelector = useMemo(
        () => (
            <TradingAccountSelector
                compact
                disabled={mode !== 'trade'}
                defaultTradingAccountId={tradingAccount?.accountId}
                sx={{
                    ml: 0.5,
                    mb: 1,
                    mr: 0.5,
                    height: '42px',
                    width: '42px',
                    flexShrink: 0,
                }}
            />
        ),
        [tradingAccount?.accountId, mode]
    )

    const placeOrderButton = useMemo(
        () => (
            <LoadingButton
                id="trade-panel-place-orders-button"
                size="large"
                sx={{
                    mx: 0.5,
                    marginBottom: 1,
                    flex: 1,
                    minWidth: 0,
                    height: '42px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
                onClick={() => {
                    if (allExecuted && !shouldPlaceSlTarget && mode == 'trade') {
                        goToPositionsHandler()
                    } else {
                        onPlaceOrderClick()
                    }
                }}
                disabled={isButtonDisabled}
                loading={loading}
                variant="contained"
                color={'primary'}
            >
                {orderButtonText}
            </LoadingButton>
        ),
        [
            allExecuted,
            shouldPlaceSlTarget,
            mode,
            goToPositionsHandler,
            onPlaceOrderClick,
            isButtonDisabled,
            loading,
            orderButtonText,
        ]
    )

    return (
        <Box sx={{ width: '100%', mt: 1 }}>
            {drawerAnchorRef && (
                <ChargesBreakdownDrawer
                    open={chargesDrawerOpen}
                    anchorRef={drawerAnchorRef}
                    items={strategyInfo.costBreakdown}
                    total={strategyInfo.brokerCharges ?? 0}
                    requiredMargin={strategyInfo.totalMargin}
                    availableMargin={primaryTradingAccountMarginAvailable}
                    brokerCode={primaryTradingAccount?.brokerCode}
                />
            )}
            <Stack direction="column" width="100%" spacing={0.5}>
                {hasActiveTradingAccount && showMarginCluster && (
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            pl: 1,
                            pr: 1,
                            boxSizing: 'border-box',
                        }}
                    >
                        {marginCluster}
                    </Box>
                )}
                <Stack direction="row" width="100%" alignItems="center">
                    {hasActiveTradingAccount && tradingAccountSelector}
                    {placeOrderButton}
                </Stack>
            </Stack>
            <OpenTradingAccountDialog
                open={openAccountDialog}
                onClose={() => setOpenAccountDialog(false)}
            />
        </Box>
    )
}


export default memo(OrderBar)